import React, { useState, useRef, useMemo, useEffect, memo } from 'react';
import classNames from 'classnames';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/high-res.css'
import {
  Country,
  State,
  City,
  ICountry,
  IState,
  ICity,
} from 'country-state-city';
import Input from '../../../../../../../../../../shared/design-system/components/input';
import InputLabel from '../../../../../../../../../../shared/design-system/components/input/input-label';
import Select from '../../../../../../../../../../shared/design-system/components/select';
import Alert from '../../../../../../../../../../shared/design-system/ui/alert';
import { Images } from '../../../../../../../../../../shared/app-constants';
import { postcodeValidator } from '../../../../../../../../../../shared/functions/postcode-validator';

export interface FormWithoutBundleProps {
  hasErrorsInContactDetailsForm: boolean;
  updateHasErrorsInContactDetailsForm: (hasErrors: boolean) => void;
  onFormValuesChange?: (values: FormValues) => void;
  initialValues?: FormValues;
}

export interface FormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  companyWebsite: string;
  address: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  phoneNumber: string;
}

const FormWithoutBundle: React.FC<FormWithoutBundleProps> = memo(({
  hasErrorsInContactDetailsForm,
  updateHasErrorsInContactDetailsForm,
  onFormValuesChange,
  initialValues,
}) => {
  const [countryCode, setCountryCode] = useState<string>(() => {
    if (!initialValues?.country) return 'us';
    const country = Country.getAllCountries().find((c) => c.name === initialValues.country);
    return country ? country.isoCode.toLowerCase() : 'us';
  });
  const phoneInputField = useRef<HTMLInputElement>(null);
  const [countries] = useState(Country.getAllCountries());
  const [states, setStates] = useState<IState[]>(() => {
    if (!initialValues?.country) return [];
    const country = Country.getAllCountries().find((c) => c.name === initialValues.country);
    return country ? State.getStatesOfCountry(country.isoCode) : [];
  });
  const [cities, setCities] = useState<ICity[]>(() => {
    if (!initialValues?.country || !initialValues?.state) return [];
    const country = Country.getAllCountries().find((c) => c.name === initialValues.country);
    if (!country) return [];
    const state = State.getStatesOfCountry(country.isoCode).find((s) => s.name === initialValues.state);
    return state ? City.getCitiesOfState(country.isoCode, state.isoCode) : [];
  });

  const [values, setValues] = useState<FormValues>(() =>
    initialValues ?? {
      firstName: '',
      lastName: '',
      companyName: '',
      companyWebsite: '',
      address: '',
      country: '',
      state: '',
      city: '',
      zipCode: '',
      phoneNumber: '',
    }
  );
  

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    companyWebsite: '',
    address: '',
    country: '',
    state: '',
    city: '',
    zipCode: '',
    phoneNumber: '',
  });

  const fieldLabels: Record<string, string> = {
    firstName: 'First name',
    lastName: 'Last name',
    companyName: 'Company name',
    companyWebsite: 'Company website',
    address: 'Address',
    country: 'Country',
    state: 'State',
    city: 'City',
    zipCode: 'ZIP / Postal code',
    phoneNumber: 'Phone number',
  };

  // Validator function with dynamic rules
  const validate = (fieldName: string, value: string) => {
    const validateUrl = (url: string) => {
      const trimmedUrl = url?.trim();
      const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}\/?$/;
      return urlRegex.test(trimmedUrl);
    };

    if (fieldName === 'state' && states.length <= 0) {
      setErrors((prev) => ({ ...prev, state: '', city: '' }));
      return '';
    }
    if (fieldName === 'state' && states.length > 0 && !values.state) {
      return 'State/Province should not be empty.';
    }
    if (fieldName === 'city' && cities.length <= 0) {
      setErrors((prev) => ({ ...prev, city: '' }));
      return '';
    }
    if (fieldName === 'city' && cities.length > 0 && !values.city) {
      return 'City should not be empty.';
    }

    if (!value || value.trim() === '') {
      return `${fieldLabels[fieldName]} should not be empty.`;
    }

    const trimmedValue = value.trim();
    const valueLength = trimmedValue.length;

    // First Name: 1-50 characters
    if (fieldName === 'firstName') {
      if (valueLength > 50) {
        return 'Maximum 50 characters allowed.';
      }
    }

    // Last Name: 1-50 characters
    if (fieldName === 'lastName') {
      if (valueLength > 50) {
        return 'Maximum 50 characters allowed.';
      }
    }

    // Company Name: 2-50 characters
    if (fieldName === 'companyName') {
      if (valueLength < 2) {
        return 'Company name must be at least 2 characters.';
      }
      if (valueLength > 50) {
        return 'Maximum 50 characters allowed.';
      }
    }

    // Company Website: 5-255 characters
    if (fieldName === 'companyWebsite') {
      if (valueLength < 5) {
        return 'Company website must be at least 5 characters.';
      }
      if (valueLength > 255) {
        return 'Maximum 255 characters allowed.';
      }
      if (!validateUrl(trimmedValue)) {
        return 'Please enter a valid website URL.';
      }
    }

    // Address: 5-300 characters
    if (fieldName === 'address') {
      if (valueLength < 5) {
        return 'Address must be at least 5 characters.';
      }
      if (valueLength > 300) {
        return 'Maximum 300 characters allowed.';
      }
    }

    // ZIP / Postal Code: 3-20 characters
    if (fieldName === 'zipCode') {
      if (valueLength < 3) {
        return 'ZIP / Postal code must be at least 3 characters.';
      }
      if (valueLength > 20) {
        return 'Maximum 20 characters allowed.';
      }
      const zipCodeValue = trimmedValue;
      const countryValue = values.country;

      if (!postcodeValidator(zipCodeValue, countryValue)) {
        return 'Enter a valid zip code.';
      }
    }

    return '';
  };
  const updateAnyErrorsInContactDetailsForm = (updatedErrors?: Partial<typeof errors>) => {
    const currentErrors = updatedErrors ? { ...errors, ...updatedErrors } : errors;

    if (
      !values.firstName ||
      !values.lastName ||
      !values.companyName ||
      !values.companyWebsite ||
      !values.address ||
      !values.country ||
      !values.zipCode ||
      !values.phoneNumber
    ) {
      if (!hasErrorsInContactDetailsForm) {
        updateHasErrorsInContactDetailsForm(true);
      }
      return;
    }

    if (states.length > 0 && !values.state) {
      if (!hasErrorsInContactDetailsForm) {
        updateHasErrorsInContactDetailsForm(true);
      }
      return;
    }

    if (cities.length > 0 && !values.city) {
      if (!hasErrorsInContactDetailsForm) {
        updateHasErrorsInContactDetailsForm(true);
      }
      return;
    }

    // Check if there are any errors
    const hasErrors = Object.values(currentErrors).some((error) => error !== '');
    if (hasErrors) {
      if (!hasErrorsInContactDetailsForm) {
        updateHasErrorsInContactDetailsForm(true);
      }
      return;
    }

    if (hasErrorsInContactDetailsForm) {
      updateHasErrorsInContactDetailsForm(false);
    }
  };

  useEffect(() => {
    updateAnyErrorsInContactDetailsForm();
  }, []);

  useEffect(() => {
    if (onFormValuesChange) {
      onFormValuesChange(values);
    }
  }, [values, onFormValuesChange]);

  const handleInputChange = (name: keyof FormValues, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  

  const handleInputBlur = (name: keyof FormValues) => {
    const errorMessage = validate(name, values[name]);
    const updatedErrors = { [name]: errorMessage };
    
    setErrors((prev) => ({
      ...prev,
      ...updatedErrors,
    }));

    updateAnyErrorsInContactDetailsForm(updatedErrors);
  };



  const handleCountryChange = (selectedCountry: ICountry | null) => {
    if (!selectedCountry || !selectedCountry.name) {
      setValues((prev) => ({
        ...prev,
        country: '',
        state: '',
        city: '',
      }));
      setStates([]);
      setCities([]);
      setErrors((prev) => {
        const updatedErrors = {
          ...prev,
          country: '',
          state: '',
          city: '',
        };
        setTimeout(() => {
          updateAnyErrorsInContactDetailsForm(updatedErrors);
        }, 0);
        return updatedErrors;
      });
      return;
    }

    setValues((prev) => ({
      ...prev,
      country: selectedCountry.name || '',
      state: '',
      city: '',
    }));

    setStates(State.getStatesOfCountry(selectedCountry.isoCode));
    setCities([]);
    setErrors((prev) => {
      const updatedErrors = {
        ...prev,
        country: '',
        state: '',
        city: '',
      };
      // Use setTimeout to ensure states are updated before validation
      setTimeout(() => {
        updateAnyErrorsInContactDetailsForm(updatedErrors);
      }, 0);
      return updatedErrors;
    });
  };

  const handleStateChange = (selectedState: IState | null) => {
    if (!selectedState || !selectedState.name) {
      setValues((prev) => ({
        ...prev,
        state: '',
        city: '',
      }));
      setCities([]);
      setErrors((prev) => {
        const updatedErrors = {
          ...prev,
          state: '',
          city: '',
        };
        updateAnyErrorsInContactDetailsForm(updatedErrors);
        return updatedErrors;
      });
      return;
    }

    const selectedCountry = countries.find(
      (c) => c.name === values.country,
    );

    setValues((prev) => ({
      ...prev,
      state: selectedState.name || '',
      city: '',
    }));

    if (selectedCountry) {
      setCities(City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode));
    } else {
      setCities([]);
    }

    setErrors((prev) => {
      const updatedErrors = {
        ...prev,
        state: '',
        city: '',
      };
      updateAnyErrorsInContactDetailsForm(updatedErrors);
      return updatedErrors;
    });
  };

  const handleCityChange = (selectedCity: ICity | null) => {
    if (!selectedCity || !selectedCity.name) {
      setValues((prev) => ({
        ...prev,
        city: '',
      }));
      setErrors((prev) => {
        const updatedErrors = {
          ...prev,
          city: '',
        };
        updateAnyErrorsInContactDetailsForm(updatedErrors);
        return updatedErrors;
      });
      return;
    }

    setValues((prev) => ({
      ...prev,
      city: selectedCity.name || '',
    }));

    setErrors((prev) => {
      const updatedErrors = {
        ...prev,
        city: '',
      };
      updateAnyErrorsInContactDetailsForm(updatedErrors);
      return updatedErrors;
    });
  };

  const handlePhoneInputChange = (value: string, data: any) => {
    setValues((prev) => ({ ...prev, phoneNumber: value }));
    setErrors((prev) => ({ ...prev, phoneNumber: '' }));

    if (data && data.countryCode !== countryCode) {
      setCountryCode(data.countryCode);
      phoneInputField.current?.focus();
    }
  };

  const countryOptions = useMemo(
    () =>
      countries.map((coun) => ({
        key: coun.name,
        name: coun.name,
      })),
    [countries],
  );

  const stateOptions = useMemo(
    () =>
      states.map((state) => ({
        key: state.name,
        name: state.name,
      })),
    [states],
  );

  const cityOptions = useMemo(
    () =>
      cities.map((city) => ({
        key: city.name,
        name: city.name,
      })),
    [cities],
  );



  return (
    <div className='form-without-bundle-container'>
      <Alert
        className="contact-details-info-alert"
        variant="info"
        description={
          <div className="contact-details-alert-content">
            <img
              src={Images.AlertCircleBlue}
              alt="Info"
              width={16}
              height={16}
            />
            <p>These contact details will be used as the registered owner for this phone number.</p>
          </div>
        }
      />
      <form className='form-without-bundle-content'>
        {/* Row 1: First Name, Last Name */}
        <div className="form-row">
          <div className="form-field">
            <Input
              label="First Name"
              placeholder="E.g John"
              value={values.firstName}
              onChange={(value?: string) => handleInputChange('firstName', value || '')}
              onBlur={() => handleInputBlur('firstName')}
              variant={errors.firstName ? Input.Variant.Error : undefined}
              caption={errors.firstName}
            />
          </div>
          <div className="form-field">
            <Input
              label="Last Name"
              placeholder="E.g Doe"
              value={values.lastName}
              onChange={(value?: string) => handleInputChange('lastName', value || '')}
              onBlur={() => handleInputBlur('lastName')}
              variant={errors.lastName ? Input.Variant.Error : undefined}
              caption={errors.lastName}
            />
          </div>
        </div>

        {/* Row 2: Company Name, Company Website */}
        <div className="form-row">
          <div className="form-field">
            <Input
              label="Company Name"
              placeholder="E.g Acme"
              value={values.companyName}
              onChange={(value?: string) => handleInputChange('companyName', value || '')}
              onBlur={() => handleInputBlur('companyName')}
              variant={errors.companyName ? Input.Variant.Error : undefined}
              caption={errors.companyName}
            />
          </div>
          <div className="form-field">
            <Input
              label="Company Website"
              placeholder="E.g https://example.com"
              value={values.companyWebsite}
              onChange={(value?: string) => handleInputChange('companyWebsite', value || '')}
              onBlur={() => handleInputBlur('companyWebsite')}
              variant={errors.companyWebsite ? Input.Variant.Error : undefined}
              caption={errors.companyWebsite}
            />
          </div>
        </div>

        {/* Row 3: Address (spans both columns) */}
        <div className="form-row form-row-full">
          <div className="form-field">
            <Input
              label="Address"
              placeholder="Street Number Name, Apartment, Unit"
              value={values.address}
              onChange={(value?: string) => handleInputChange('address', value || '')}
              onBlur={() => handleInputBlur('address')}
              variant={errors.address ? Input.Variant.Error : undefined}
              caption={errors.address}
            />
          </div>
        </div>

        {/* Row 4: Country, State / Province */}
        <div className="form-row">
          <div className={`form-field ${errors.country ? 'has-error' : ''}`}>
            <InputLabel text="Country" place={InputLabel.Place.Before} />
            <Select<{ key: string; name: string }>
              className={`country-select-dropdown ${
                errors.country ? 'has-error' : ''
              }`}
              options={countryOptions}
              selectedOptionKey={values.country || null}
              optionRenderer={(option) => <span>{option.name}</span>}
              onChange={([option]) => {
                if (!option) {
                  handleCountryChange(null);
                  return;
                }
                const selectedCountry = countries?.find(
                  (c) => c?.name === option?.name,
                );
                if (selectedCountry) {
                  handleCountryChange(selectedCountry);
                }
              }}
              selectedOptionRenderer={([option]) => (
                <span>{option?.name || ''}</span>
              )}
              placeholder="Select Country"
              showSearch
              searchPlaceholder="Search"
              filterOption={(search, option) =>
                option?.name?.toLowerCase().includes(search.toLowerCase())
              }
              onExit={() => handleInputBlur('country')}
              autoFocusSearch={true}
            />
            {errors.country && (
              <InputLabel
                text={errors.country}
                place={InputLabel.Place.After}
              />
            )}
          </div>
          <div className={`form-field ${errors.state ? 'has-error' : ''}`}>
            <InputLabel
              text="State / Province"
              place={InputLabel.Place.Before}
            />
            <Select<{ key: string; name: string }>
              className={`state-select-dropdown ${
                errors.state ? 'has-error' : ''
              }`}
              options={stateOptions}
              selectedOptionKey={values.state || null}
              optionRenderer={(option) => <span>{option.name}</span>}
              onChange={([option]) => {
                if (!option) {
                  handleStateChange(null);
                  return;
                }
                const selectedState = states?.find(
                  (s) => s?.name === option?.name,
                );
                if (selectedState) {
                  handleStateChange(selectedState);
                }
              }}
              selectedOptionRenderer={([option]) => (
                <span>{option?.name || ''}</span>
              )}
              placeholder="Select State / Province"
              showSearch
              searchPlaceholder="Search"
              filterOption={(search, option) =>
                option?.name?.toLowerCase().includes(search.toLowerCase())
              }
              onExit={() => handleInputBlur('state')}
              autoFocusSearch={true}
            />
            {errors.state && (
              <InputLabel
                text={errors.state}
                place={InputLabel.Place.After}
              />
            )}
          </div>
        </div>

        {/* Row 5: City, ZIP / Postal Code */}
        <div className="form-row">
          <div className={`form-field ${errors.city ? 'has-error' : ''}`}>
            <InputLabel
              text="City"
              place={InputLabel.Place.Before}
            />
            <Select<{ key: string; name: string }>
              className={`city-select-dropdown ${
                errors.city ? 'has-error' : ''
              }`}
              options={cityOptions}
              selectedOptionKey={values.city || null}
              optionRenderer={(option) => <span>{option.name}</span>}
              onChange={([option]) => {
                if (!option) {
                  handleCityChange(null);
                  return;
                }
                const selectedCity = cities?.find(
                  (c) => c?.name === option?.name,
                );
                if (selectedCity) {
                  handleCityChange(selectedCity);
                }
              }}
              selectedOptionRenderer={([option]) => (
                <span>{option?.name || ''}</span>
              )}
              placeholder="Select City"
              showSearch
              searchPlaceholder="Search"
              filterOption={(search, option) =>
                option?.name?.toLowerCase().includes(search.toLowerCase())
              }
              onExit={() => handleInputBlur('city')}
              autoFocusSearch={true}
            />
            {errors.city && (
              <InputLabel
                text={errors.city}
                place={InputLabel.Place.After}
              />
            )}
          </div>
          <div className="form-field">
            <Input
              type="text"
              label="ZIP / Postal Code"
              placeholder="E.g 10001"
              value={values.zipCode}
              onChange={(value?: string) => {
                // Only digits 0-9 are allowed
                const onlyNumbers = value?.replace(/[^0-9]/g, '') || '';
                handleInputChange('zipCode', onlyNumbers);
              }}
              onBlur={() => handleInputBlur('zipCode')}
              variant={errors.zipCode ? Input.Variant.Error : undefined}
              caption={errors.zipCode}
            />
          </div>
        </div>

        {/* Row 6: Phone Number */}
        <div className="form-row form-row-full">
          <div className="form-field">
            <InputLabel text="Phone Number" place={InputLabel.Place.Before} />
            <PhoneInput
              country={countryCode}
              value={values.phoneNumber}
              onChange={handlePhoneInputChange}
              onBlur={() => handleInputBlur('phoneNumber')}
              containerClass={classNames([
                'phone-form-input',
                { 'input-error': errors.phoneNumber },
              ])}
              dropdownClass="phone-input-country-dropdown-top"
              inputProps={{
                tabIndex: 0,
                name: 'phone',
                ref: phoneInputField,
              }}
            />
            {errors.phoneNumber && (
              <InputLabel
                text={errors.phoneNumber}
                place={InputLabel.Place.After}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
});

FormWithoutBundle.displayName = 'FormWithoutBundle';

export default FormWithoutBundle;
